import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { transactionAPI, itemAPI, paymentAPI } from '../services/api';
import { Edit2, Trash2, Download } from 'lucide-react';
import InfoTooltip from '../components/common/InfoTooltip';
import { useAuth } from '../contexts/FirebaseAuthContext';

const SellerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSales: 0,
    pendingShipments: 0,
    totalRevenue: 0,
    pendingEarnings: 0,
    activeListings: 0,
  });
  const [recentSales, setRecentSales] = useState([]);
  const [pendingShipments, setPendingShipments] = useState([]);
  const [activeListings, setActiveListings] = useState([]);
  const [draftItems, setDraftItems] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch sales transactions (as seller)
      const salesResponse = await transactionAPI.getTransactions({ role: 'seller' });
      const allSales = salesResponse.data.data || [];

      // Calculate stats
      const totalSales = allSales.length;
      const pendingShips = allSales.filter(
        (t) => t.status === 'paid' && t.purchaseType === 'shipping'
      );
      const completedSales = allSales.filter((t) => t.status === 'completed');
      const totalRevenue = completedSales.reduce(
        (sum, t) => sum + (t.sellerPayout || 0),
        0
      );

      // Pending earnings = money the seller is owed but hasn't received yet.
      // A sale owes the seller until the payout actually transfers (payoutStatus
      // 'paid' / a stripeTransferId). This covers in-escrow sales AND released-
      // but-unpaid ones, and is robust to older transactions that predate the
      // payoutStatus field.
      const pendingEarnings = allSales
        .filter(
          (t) =>
            ['paid', 'shipped', 'delivered', 'completed'].includes(t.status) &&
            t.payoutStatus !== 'paid' &&
            !t.stripeTransferId
        )
        .reduce((sum, t) => sum + (t.sellerPayout || 0), 0);

      // Fetch current user's listings
      const listingsResponse = await itemAPI.getItems({
        user: user._id,
        limit: 100  // Increased limit to get all user's listings
      });
      const allListings = listingsResponse.data.data || [];

      const listings = allListings.filter(
        item => item.status === 'active' || item.status === 'in-escrow'
      );

      const drafts = allListings.filter(item => item.status === 'draft');

      setStats({
        totalSales,
        pendingShipments: pendingShips.length,
        totalRevenue: totalRevenue / 100, // Convert from cents
        pendingEarnings: pendingEarnings / 100,
        activeListings: listings.length,
      });

      // Set recent sales (last 5)
      setRecentSales(allSales.slice(0, 5));

      // Set pending shipments
      setPendingShipments(pendingShips);

      // Set active listings
      setActiveListings(listings.slice(0, 5));

      // Set draft items
      setDraftItems(drafts);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [fetchDashboardData, user]);

  const getStatusBadge = (status) => {
    const statusMap = {
      paid: { text: 'Paid - Ready to Ship', color: '#2196F3' },
      shipped: { text: 'Shipped', color: '#FF9800' },
      delivered: { text: 'Delivered', color: '#9C27B0' },
      completed: { text: 'Completed', color: '#4CAF50' },
      disputed: { text: 'Disputed', color: '#f44336' },
    };

    const statusInfo = statusMap[status] || { text: status, color: '#757575' };

    return (
      <span
        style={{
          backgroundColor: statusInfo.color,
          color: 'white',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
        }}
      >
        {statusInfo.text}
      </span>
    );
  };

  const getItemStatusBadge = (status) => {
    const statusMap = {
      draft: { text: 'Draft - Add Images', color: '#757575' },
      active: { text: 'Active', color: '#4CAF50' },
      'in-escrow': { text: 'In Escrow', color: '#FF9800' },
      pending: { text: 'Pending', color: '#2196F3' },
      sold: { text: 'Sold', color: '#9C27B0' },
    };

    const statusInfo = statusMap[status] || { text: status, color: '#757575' };

    return (
      <span
        style={{
          backgroundColor: statusInfo.color,
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: '600',
          textTransform: 'uppercase',
        }}
      >
        {statusInfo.text}
      </span>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleDeleteItem = async (e, itemId) => {
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
      return;
    }

    try {
      await itemAPI.deleteItem(itemId);
      // Refresh dashboard data
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to delete item:', error);
      alert('Failed to delete item. Please try again.');
    }
  };

  const handleEditItem = (e, itemId) => {
    e.stopPropagation();
    navigate(`/edit-listing/${itemId}`);
  };

  const [payingId, setPayingId] = useState(null);

  // A sale is claimable when the buyer released funds (completed) but the
  // payout hasn't transferred yet.
  const isPayoutClaimable = (t) =>
    t.status === 'completed' && t.payoutStatus !== 'paid' && !t.stripeTransferId && !t.fundsHeldInEscrow;

  const handleGetPaid = async (e, transactionId) => {
    e.stopPropagation();
    try {
      setPayingId(transactionId);
      const res = await transactionAPI.retryPayout(transactionId);
      alert(res.data?.message || 'Payout sent to your Stripe account!');
      fetchDashboardData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to send payout. Make sure your Stripe account is connected in Payment Settings.');
    } finally {
      setPayingId(null);
    }
  };

  const handleMarkShipped = async (e, transactionId) => {
    e.stopPropagation();

    if (!window.confirm('Mark this item as shipped? The buyer will be notified with the tracking info from the prepaid label.')) {
      return;
    }

    try {
      // Tracking/carrier come from the auto-purchased label — no manual entry needed.
      await paymentAPI.markShipped(transactionId, {});
      alert('Item marked as shipped! The buyer has been notified.');
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to mark as shipped:', error);
      alert(error.response?.data?.error || 'Failed to mark item as shipped. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px', textAlign: 'center' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '40px', paddingBottom: '60px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '10px' }}>
          Seller Dashboard
        </h1>
        <p className="text-muted">Manage your listings and sales</p>
      </div>

      {/* Stats Cards */}
      <div className="row" style={{ marginBottom: '30px' }}>
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div className="card-body">
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                Total Sales
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#4CAF50' }}>
                {stats.totalSales}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div className="card-body">
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                Pending Shipments
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#FF9800' }}>
                {stats.pendingShipments}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div className="card-body">
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                Total Revenue
                <InfoTooltip text="Your net earnings from completed sales — the item price minus our commission. Shipping and service fees are not part of your payout." />
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#2196F3' }}>
                ${stats.totalRevenue.toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div className="card-body">
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                Pending
                <InfoTooltip text="Money you've earned that hasn't paid out yet — funds held in escrow until the buyer confirms delivery, plus any payouts waiting on your Stripe connection. This reaches your bank once released." />
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#FF9800' }}>
                ${stats.pendingEarnings.toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div className="card-body">
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                Active Listings
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#9C27B0' }}>
                {stats.activeListings}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Shipments Section */}
      {pendingShipments.length > 0 && (
        <div className="card" style={{ marginBottom: '30px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div className="card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
                ⚠️ Pending Shipments
              </h3>
              <Link to="/transactions" style={{ textDecoration: 'none', color: '#2196F3' }}>
                View All
              </Link>
            </div>

            <div style={{ backgroundColor: '#fff3cd', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#856404' }}>
                <strong>Action Required:</strong> You have {pendingShipments.length} order(s) waiting to be shipped.
              </p>
            </div>

            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Item</th>
                    <th>Buyer</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingShipments.map((transaction) => (
                    <tr key={transaction._id}>
                      <td>
                        <code style={{ fontSize: '12px' }}>
                          {transaction._id.slice(-8)}
                        </code>
                      </td>
                      <td>
                        <Link
                          to={`/items/${transaction.item._id}`}
                          style={{ textDecoration: 'none', color: '#333' }}
                        >
                          {transaction.item.title}
                        </Link>
                        {transaction.item.recommendedBox?.name && (
                          <div style={{ fontSize: '11px', color: 'var(--textSecondary)', marginTop: '2px' }}>
                            📦 Ship in: {transaction.item.recommendedBox.name}
                            {transaction.item.recommendedBox.length ? ` (${transaction.item.recommendedBox.length}×${transaction.item.recommendedBox.width}×${transaction.item.recommendedBox.height} in)` : ''}
                          </div>
                        )}
                      </td>
                      <td>{transaction.buyer.name}</td>
                      <td>${(transaction.amount / 100).toFixed(2)}</td>
                      <td>{formatDate(transaction.createdAt)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {transaction.shippingLabel?.labelUrl && (
                            <a
                              href={transaction.shippingLabel.labelUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="btn btn-sm btn-success"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                              title="Download the prepaid shipping label PDF"
                            >
                              <Download size={14} />
                              Download Label
                            </a>
                          )}
                          <button
                            onClick={(e) => handleMarkShipped(e, transaction._id)}
                            className="btn btn-sm btn-primary"
                          >
                            Mark as Shipped
                          </button>
                          <button
                            onClick={() => navigate(`/transactions/${transaction._id}`)}
                            className="btn btn-sm btn-outline-secondary"
                          >
                            View Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Recent Sales */}
      <div className="card" style={{ marginBottom: '30px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
              Recent Sales
            </h3>
            <Link to="/transactions" style={{ textDecoration: 'none', color: '#2196F3' }}>
              View All
            </Link>
          </div>

          {recentSales.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
              <p>No sales yet</p>
              <Link to="/create-listing" className="btn btn-primary" style={{ marginTop: '10px' }}>
                Create Your First Listing
              </Link>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Item</th>
                    <th>Buyer</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Payout</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSales.map((transaction) => (
                    <tr
                      key={transaction._id}
                      onClick={() => navigate(`/transactions/${transaction._id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <code style={{ fontSize: '12px' }}>
                          {transaction._id.slice(-8)}
                        </code>
                      </td>
                      <td>{transaction.item.title}</td>
                      <td>{transaction.buyer.name}</td>
                      <td>${(transaction.sellerPayout / 100).toFixed(2)}</td>
                      <td>{getStatusBadge(transaction.status)}</td>
                      <td>{formatDate(transaction.createdAt)}</td>
                      <td>
                        {transaction.payoutStatus === 'paid' || transaction.stripeTransferId ? (
                          <span style={{ color: '#4CAF50', fontSize: '13px', fontWeight: 600 }}>Paid</span>
                        ) : isPayoutClaimable(transaction) ? (
                          <button
                            onClick={(e) => handleGetPaid(e, transaction._id)}
                            disabled={payingId === transaction._id}
                            className="btn btn-sm"
                            style={{ background: '#0fa573', color: 'white', fontSize: '13px', padding: '6px 12px' }}
                          >
                            {payingId === transaction._id ? 'Paying…' : 'Get Paid'}
                          </button>
                        ) : (
                          <span style={{ color: '#999', fontSize: '13px' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Draft Items Section */}
      {draftItems.length > 0 && (
        <div className="card" style={{ marginBottom: '30px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div className="card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
                📝 Draft Items - Add Images to Publish
              </h3>
            </div>

            <div style={{ backgroundColor: '#e3f2fd', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#1565c0' }}>
                <strong>Action Required:</strong> You have {draftItems.length} draft item(s). Click "Edit" to add images and publish them.
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '16px',
            }}>
              {draftItems.map((item) => (
                <div key={item._id}>
                  <div
                    className="card"
                    style={{ cursor: 'pointer', border: '2px dashed #999', position: 'relative', overflow: 'hidden' }}
                    onClick={() => navigate(`/edit-listing/${item._id}`)}
                  >
                    <div style={{
                      width: '100%',
                      paddingBottom: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        color: '#999',
                      }}>
                        <div style={{ fontSize: '48px', marginBottom: '8px' }}>📷</div>
                        <div style={{ fontSize: '12px' }}>No images</div>
                      </div>
                    </div>
                    <div className="card-body" style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '6px', marginBottom: '6px' }}>
                        <h5 style={{ fontSize: '14px', fontWeight: '600', margin: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.title}
                        </h5>
                        {getItemStatusBadge(item.status)}
                      </div>
                      <p style={{ fontSize: '16px', fontWeight: '700', color: '#4CAF50', margin: 0 }}>
                        ${item.price}
                      </p>
                      <p style={{ fontSize: '11px', color: '#999', marginTop: '4px', marginBottom: '8px' }}>
                        Created {formatDate(item.createdAt)}
                      </p>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={(e) => handleEditItem(e, item._id)}
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            padding: '8px',
                            background: '#2196F3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                          }}
                        >
                          <Edit2 size={14} />
                          Add Images
                        </button>
                        <button
                          onClick={(e) => handleDeleteItem(e, item._id)}
                          style={{
                            padding: '8px',
                            background: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Active Listings */}
      <div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
              Active Listings
            </h3>
            <Link to="/create-listing" className="btn btn-primary">
              Create New Listing
            </Link>
          </div>

          {activeListings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
              <p>No active listings</p>
              <Link to="/create-listing" className="btn btn-primary" style={{ marginTop: '10px' }}>
                Create Your First Listing
              </Link>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '16px',
            }}>
              {activeListings.map((item) => (
                <div key={item._id}>
                  <div
                    className="card"
                    style={{ cursor: 'pointer', border: '1px solid #e0e0e0', position: 'relative', overflow: 'hidden' }}
                    onClick={() => navigate(`/items/${item._id}`)}
                  >
                    <div style={{
                      width: '100%',
                      paddingBottom: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                    }}>
                      <img
                        src={item.images?.[0]?.thumbnail || item.images?.[0]?.fullSize || '/placeholder.png'}
                        alt={item.title}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </div>
                    <div className="card-body" style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '6px', marginBottom: '6px' }}>
                        <h5 style={{ fontSize: '14px', fontWeight: '600', margin: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.title}
                        </h5>
                        {getItemStatusBadge(item.status)}
                      </div>
                      <p style={{ fontSize: '16px', fontWeight: '700', color: '#4CAF50', margin: 0 }}>
                        ${item.price}
                      </p>
                      <p style={{ fontSize: '11px', color: '#999', marginTop: '4px', marginBottom: '8px' }}>
                        Listed {formatDate(item.createdAt)}
                      </p>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={(e) => handleEditItem(e, item._id)}
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            padding: '6px',
                            background: '#2196F3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                          }}
                        >
                          <Edit2 size={14} />
                          Edit
                        </button>
                        <button
                          onClick={(e) => handleDeleteItem(e, item._id)}
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            padding: '6px',
                            background: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                          }}
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerDashboard;
